import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { useDashboardProducts, type DashboardProduct } from "../hooks/useDashboardProducts";
import { useMrr } from "../hooks/useMrr";
import { useSubscriberGrowth } from "../hooks/useSubscriberGrowth";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

const columnHelper = createColumnHelper<DashboardProduct>();

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);
}

const SINGULAR_LABELS: Record<string, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
  year: "Yearly",
};

function formatBillingInterval(interval: string, count: number) {
  if (count === 1) {
    return SINGULAR_LABELS[interval] ?? interval;
  }
  return `Every ${count} ${interval}s`;
}

const columns = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <Link to={`/products/${info.row.original.id}`} className="text-indigo-600 hover:text-indigo-800 font-medium">
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor("amount", {
    header: "Price",
    cell: (info) => {
      const amount = info.getValue();
      const currency = info.row.original.currency;
      return amount !== null && currency !== null ? formatCurrency(amount, currency) : "—";
    },
  }),
  columnHelper.accessor("billing_interval", {
    header: "Billing Period",
    cell: (info) => {
      const interval = info.getValue();
      const count = info.row.original.billing_interval_count;
      return interval && count !== null ? formatBillingInterval(interval, count) : "—";
    },
  }),
  columnHelper.accessor("total_subscribers", {
    header: "Total Subscribers",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => (row.amount ?? 0) * row.total_subscribers, {
    id: "revenue",
    header: "Billing Period Revenue",
    cell: (info) => {
      const currency = info.row.original.currency;
      return currency ? formatCurrency(info.getValue(), currency) : "—";
    },
  }),
];

const CHART_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16",
];

export function Dashboard() {
  useEffect(() => { document.title = "Products | PayWithPull"; }, []);
  const { user } = useUser();
  const { data: products = [], isLoading, error } = useDashboardProducts();
  const { data: mrrData = [] } = useMrr();
  const { data: subscriberData = [] } = useSubscriberGrowth();

  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: "revenue", desc: true }],
    },
  });

  const mrrChartData = {
    labels: mrrData.map((d) => d.month),
    datasets: [
      {
        label: "MRR (£)",
        data: mrrData.map((d) => d.mrr),
        borderColor: "#6366f1",
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: "#6366f1",
      },
    ],
  };

  const subscriberChartData = {
    labels: subscriberData.map((d) => d.month),
    datasets: [
      {
        label: "Total Subscribers",
        data: subscriberData.map((d) => d.total_subscribers),
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: "#10b981",
      },
    ],
  };

  const revenueByProduct = {
    labels: products.map((p) => p.name),
    datasets: [
      {
        data: products.map((p) => (p.amount ?? 0) * p.total_subscribers),
        backgroundColor: products.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderWidth: 0,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#9ca3af", font: { size: 11 } },
      },
      y: {
        grid: { color: "#f3f4f6" },
        ticks: { color: "#9ca3af", font: { size: 11 } },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { color: "#6b7280", font: { size: 12 }, padding: 16 },
      },
    },
  };

  return (
    <DashboardLayout>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Welcome back{user?.firstName ? `, ${user.firstName}` : ""}
        </h1>
        <p className="text-gray-500 mb-8">Here's an overview of your products.</p>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
              MRR Over Time
            </h2>
            <div className="h-56">
              {mrrData.length > 0 ? (
                <Line data={mrrChartData} options={lineOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No MRR data yet
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
              Subscriber Growth
            </h2>
            <div className="h-56">
              {subscriberData.length > 0 ? (
                <Line data={subscriberChartData} options={lineOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No subscriber data yet
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
              Revenue by Product
            </h2>
            <div className="h-56">
              {products.some((p) => p.total_subscribers > 0) ? (
                <Doughnut data={revenueByProduct} options={doughnutOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No revenue data yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400">Loading products...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error.message}</div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No products yet.</div>
          ) : (
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-gray-200 bg-gray-50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
    </DashboardLayout>
  );
}
